import '@fontsource/space-grotesk/400.css';
import '@fontsource/space-grotesk/500.css';
import '@fontsource/space-grotesk/600.css';
import '@fontsource/space-grotesk/700.css';
import '@fontsource/jetbrains-mono/400.css';
import '@fontsource/jetbrains-mono/500.css';
import '@fontsource/newsreader/400.css';
import '@fontsource/newsreader/500.css';
import '@fontsource/newsreader/600.css';
import '../../components/wiki/graph/styles.css';

import { WikiMarkdown } from '@/components/wiki/WikiMarkdown';
import { GraphRenderer } from '@/components/wiki/graph/renderer';
import { GraphSim } from '@/components/wiki/graph/sim';
import type {
  DarkPalette,
  GraphCategory,
  GraphEdgeData,
  GraphNodeData,
  Palette,
  SimNode,
  Theme,
} from '@/components/wiki/graph/types';
import { api } from '@/lib/api';
import { hexToHue } from '@/lib/color';
import type { WikiGraphResponse } from '@innermate/shared';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const DARK_PALETTES: Array<{ id: DarkPalette; label: string }> = [
  { id: 'nebula', label: 'Nebula' },
  { id: 'aurora', label: 'Aurora' },
  { id: 'ember', label: 'Ember' },
];

const UNCATEGORIZED_ID = '__uncategorized';

function mapGraphData(data: WikiGraphResponse): {
  nodes: GraphNodeData[];
  edges: GraphEdgeData[];
  categories: GraphCategory[];
} {
  const categories: GraphCategory[] = data.categories.map((c) => ({
    id: c.slug,
    label: c.name,
    hue: hexToHue(c.color),
  }));
  // uncategorized 는 (있으면) 별도 cat
  const hasUncategorized = data.nodes.some((n) => !n.categorySlug);
  if (hasUncategorized) {
    categories.push({ id: UNCATEGORIZED_ID, label: '분류 없음', hue: 220 });
  }

  const nodes: GraphNodeData[] = data.nodes.map((n) => ({
    id: n.slug,
    title: n.title,
    category: n.categorySlug ?? UNCATEGORIZED_ID,
    isCenter: n.isCenter,
    isCategoryHub: n.isCategoryHub,
    serverDegree: n.degree,
  }));
  const edges: GraphEdgeData[] = data.edges
    .filter((e) => e.resolved)
    .map((e) => ({ source: e.fromSlug, target: e.toSlug }));
  return { nodes, edges, categories };
}

export function WikiGraph() {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const minimapRef = useRef<HTMLCanvasElement | null>(null);
  const simRef = useRef<GraphSim | null>(null);
  const rendererRef = useRef<GraphRenderer | null>(null);
  const navigate = useNavigate();

  const [theme, setTheme] = useState<Theme>('dark');
  const [palette, setPalette] = useState<Palette>('nebula');
  const themeRef = useRef(theme);
  themeRef.current = theme;

  const [selected, setSelected] = useState<SimNode | null>(null);
  const [hover, setHover] = useState<{ node: SimNode; sx: number; sy: number } | null>(null);
  const [search, setSearch] = useState('');
  const [activeCats, setActiveCats] = useState<Set<string>>(new Set());
  const [focusMode, setFocusMode] = useState(false);
  const [stats, setStats] = useState({ visible: 0, edges: 0 });

  const graphQuery = useQuery({
    queryKey: ['wiki', 'graph'],
    queryFn: api.wikiGraph,
    staleTime: 60_000,
  });

  const mapped = useMemo(
    () => (graphQuery.data ? mapGraphData(graphQuery.data) : null),
    [graphQuery.data],
  );

  // 페이지 markdown lazy load (panel 열림 시)
  const pageQuery = useQuery({
    queryKey: ['wiki', 'page', selected?.id],
    queryFn: () => (selected ? api.wikiPage(selected.id) : Promise.resolve(null)),
    enabled: !!selected,
    staleTime: 60_000,
  });

  // 임베딩 기반 의미 유사 페이지
  const similarQuery = useQuery({
    queryKey: ['wiki', 'similar', selected?.id],
    queryFn: () =>
      selected
        ? api.wikiSimilarPages(selected.id, 5)
        : Promise.resolve({ enabled: false, results: [] as never }),
    enabled: !!selected,
    staleTime: 60_000,
    retry: false,
  });

  // body data-theme/palette 적용 + 떠날 때 원복
  useEffect(() => {
    const prevTheme = document.body.dataset.theme;
    const prevPalette = document.body.dataset.palette;
    document.body.dataset.theme = theme;
    document.body.dataset.palette = palette;
    return () => {
      if (prevTheme !== undefined) document.body.dataset.theme = prevTheme;
      else delete document.body.dataset.theme;
      if (prevPalette !== undefined) document.body.dataset.palette = prevPalette;
      else delete document.body.dataset.palette;
    };
  }, [theme, palette]);

  // 카테고리 모두 활성 — graph 데이터 로드 후
  useEffect(() => {
    if (mapped) {
      setActiveCats(new Set(mapped.categories.map((c) => c.id)));
    }
  }, [mapped]);

  // sim + renderer 초기화
  useEffect(() => {
    if (!mapped || !canvasRef.current) return;

    const sim = new GraphSim(mapped.nodes, mapped.edges);
    simRef.current = sim;
    const renderer = new GraphRenderer(
      canvasRef.current,
      sim,
      mapped.categories,
      () => themeRef.current,
    );
    rendererRef.current = renderer;

    // 초기 settle
    for (let i = 0; i < 220; i++) sim.step(1);

    let raf = 0;
    const tick = () => {
      try {
        sim.step(0.55);
        renderer.draw();
        if (minimapRef.current) renderer.drawMinimap(minimapRef.current);
      } catch {}
    };
    const loop = () => {
      tick();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    const interval = setInterval(tick, 1000 / 30);
    return () => {
      cancelAnimationFrame(raf);
      clearInterval(interval);
      renderer.destroy();
      simRef.current = null;
      rendererRef.current = null;
    };
  }, [mapped]);

  // 카테고리 필터 + 검색 → renderer 적용
  useEffect(() => {
    const renderer = rendererRef.current;
    if (!renderer || !mapped) return;
    renderer.activeCats = activeCats.size === mapped.categories.length ? null : new Set(activeCats);
    let hits: Set<string> | null = null;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      hits = new Set();
      for (const n of mapped.nodes) {
        if (n.id.toLowerCase().includes(q) || n.title.toLowerCase().includes(q)) {
          hits.add(n.id);
        }
      }
    }
    renderer.searchHits = hits;
    renderer.focusId = focusMode && selected ? selected.id : null;
    const visibleCount = mapped.nodes.filter(
      (n) => renderer.activeCats === null || renderer.activeCats.has(n.category),
    ).length;
    setStats({ visible: visibleCount, edges: mapped.edges.length });
  }, [mapped, activeCats, search, focusMode, selected]);

  // 마우스
  useEffect(() => {
    const stage = stageRef.current;
    const renderer = rendererRef.current;
    if (!stage || !renderer) return;

    let dragging = false;
    let dragMoved = false;
    let dragNode: SimNode | null = null;
    let lastX = 0;
    let lastY = 0;

    const getXY = (e: MouseEvent) => {
      const rect = stage.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const onMove = (e: MouseEvent) => {
      const { x, y } = getXY(e);
      if (dragging) {
        const dx = x - lastX;
        const dy = y - lastY;
        if (Math.hypot(dx, dy) > 2) dragMoved = true;
        if (dragNode) {
          const w = renderer.screenToWorld(x, y);
          dragNode.x = w.x;
          dragNode.y = w.y;
          dragNode.vx = 0;
          dragNode.vy = 0;
        } else {
          renderer.pan(dx, dy);
        }
        lastX = x;
        lastY = y;
      } else {
        const n = renderer.pickNode(x, y);
        renderer.hoverId = n ? n.id : null;
        if (n) {
          setHover({ node: n, sx: e.clientX, sy: e.clientY });
          stage.style.cursor = 'pointer';
        } else {
          setHover(null);
          stage.style.cursor = '';
        }
      }
    };
    const onDown = (e: MouseEvent) => {
      const { x, y } = getXY(e);
      const n = renderer.pickNode(x, y);
      dragging = true;
      dragMoved = false;
      dragNode = n;
      if (n) n.pinned = true;
      lastX = x;
      lastY = y;
      stage.classList.add('dragging');
    };
    const onUp = (e: MouseEvent) => {
      if (!dragging) return;
      const { x, y } = getXY(e);
      if (!dragMoved) {
        const n = renderer.pickNode(x, y);
        if (n) {
          setSelected(n);
        } else {
          setSelected(null);
          setFocusMode(false);
        }
      }
      if (dragNode) {
        const dn = dragNode;
        setTimeout(() => {
          dn.pinned = false;
        }, 800);
      }
      dragging = false;
      dragNode = null;
      stage.classList.remove('dragging');
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const { x, y } = getXY(e);
      const factor = Math.exp(-e.deltaY * 0.0015);
      renderer.cam.targetScale = Math.max(0.18, Math.min(3.5, renderer.cam.targetScale * factor));
      const before = renderer.screenToWorld(x, y);
      renderer.cam.scale = renderer.cam.targetScale;
      const after = renderer.screenToWorld(x, y);
      renderer.cam.x += before.x - after.x;
      renderer.cam.y += before.y - after.y;
    };

    stage.addEventListener('mousemove', onMove);
    stage.addEventListener('mousedown', onDown);
    window.addEventListener('mouseup', onUp);
    stage.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      stage.removeEventListener('mousemove', onMove);
      stage.removeEventListener('mousedown', onDown);
      window.removeEventListener('mouseup', onUp);
      stage.removeEventListener('wheel', onWheel);
    };
  }, [mapped]);

  // Keyboard
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isInput = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA';
      if (e.key === 'Escape') {
        setSelected(null);
        setFocusMode(false);
      }
      if (e.key === '/' && !isInput) {
        e.preventDefault();
        document.getElementById('aurora-search')?.focus();
      }
      if (e.key.toLowerCase() === 'f' && selected && !isInput) {
        setFocusMode((f) => !f);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selected]);

  const toggleCat = useCallback(
    (id: string) => {
      if (!mapped) return;
      setActiveCats((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          if (next.size === 1) {
            return new Set(mapped.categories.map((c) => c.id));
          }
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
    },
    [mapped],
  );

  const focusOn = useCallback((slug: string) => {
    const sim = simRef.current;
    const renderer = rendererRef.current;
    if (!sim || !renderer) return;
    const node = sim.byId[slug];
    if (!node) return;
    setSelected(node);
    renderer.cam.x = node.x;
    renderer.cam.y = node.y;
    renderer.cam.targetScale = Math.max(1.2, renderer.cam.scale);
  }, []);

  const related = useMemo<SimNode[]>(() => {
    if (!selected || !rendererRef.current || !simRef.current) return [];
    const links = rendererRef.current.linkMap.get(selected.id);
    if (!links) return [];
    return Array.from(links)
      .map((id) => simRef.current?.byId[id])
      .filter((n): n is SimNode => Boolean(n));
  }, [selected]);

  const selectedCatHue = useMemo(() => {
    if (!selected || !mapped) return 280;
    return mapped.categories.find((c) => c.id === selected.category)?.hue ?? 280;
  }, [selected, mapped]);

  if (graphQuery.isLoading) {
    return (
      <div className="app">
        <div className="cosmic-loading">
          <div className="core">
            <div className="ring" />
            <div className="ititle">지식 그래프</div>
            <div className="isub">
              우주 펼치는 중
              <span className="dots">
                <span /> <span /> <span />
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }
  if (graphQuery.isError || !mapped) {
    return (
      <div className="app" style={{ display: 'grid', placeItems: 'center' }}>
        그래프를 불러오지 못했습니다.
      </div>
    );
  }

  const L = theme === 'dark' ? 0.72 : 0.5;

  return (
    <div className={`app ${selected ? 'panel-open' : ''}`}>
      <div className="stage" ref={stageRef}>
        <canvas ref={canvasRef} />
      </div>

      {/* Top chrome */}
      <div className="topbar">
        <div className="brand">
          <div className="brand-mark" />
          <div>
            <strong>지식 그래프</strong>
          </div>
        </div>
        <div className="search">
          <span style={{ color: 'var(--fg-dim)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
            ⌕
          </span>
          <input
            id="aurora-search"
            placeholder="문서 / 슬러그 검색"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
          />
          <span className="kbd">/</span>
        </div>
      </div>

      {/* Filter chips */}
      <div className="filterbar">
        <div className="filter-row">
          {mapped.categories.map((c) => {
            const on = activeCats.has(c.id);
            const color = `oklch(${L} 0.15 ${c.hue})`;
            const count = mapped.nodes.filter((n) => n.category === c.id).length;
            return (
              <button
                key={c.id}
                type="button"
                className={`chip ${on ? 'on' : 'off'}`}
                style={{ color }}
                onClick={() => toggleCat(c.id)}
                title={`${c.label} — ${count}`}
              >
                <span className="dot" />
                {c.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Stats */}
      <div className="stats">
        <div className="stat">
          <strong>{stats.visible}</strong> nodes
        </div>
        <div className="stat">
          <strong>{stats.edges}</strong> edges
        </div>
        <div className="stat">
          <strong>{mapped.categories.length}</strong> clusters
        </div>
        {focusMode && selected ? (
          <div className="stat" style={{ color: 'var(--fg)' }}>
            · focus: <strong>{selected.title}</strong>
          </div>
        ) : null}
      </div>

      {/* Bottom-right */}
      <div className="bottom-right">
        <div className="minimap">
          <canvas ref={minimapRef} />
        </div>
        <div className="toolstack">
          <button
            type="button"
            className={focusMode ? 'active' : ''}
            disabled={!selected}
            onClick={() => setFocusMode((f) => !f)}
            style={{ opacity: selected ? 1 : 0.4 }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: 'currentColor',
                display: 'inline-block',
              }}
            />
            FOCUS {focusMode ? 'ON' : 'OFF'}
          </button>
          <button
            type="button"
            onClick={() => {
              const r = rendererRef.current;
              if (r) {
                r.cam.x = 0;
                r.cam.y = 0;
                r.cam.targetScale = 1.0;
              }
            }}
          >
            ⤢ RECENTER
          </button>
          <button type="button" onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}>
            {theme === 'dark' ? '☾' : '☀'} {theme.toUpperCase()}
          </button>
          {theme === 'dark' ? (
            <button
              type="button"
              onClick={() => {
                const idx = DARK_PALETTES.findIndex((p) => p.id === palette);
                const next = DARK_PALETTES[(idx + 1) % DARK_PALETTES.length];
                if (next) setPalette(next.id);
              }}
            >
              ◐ {DARK_PALETTES.find((p) => p.id === palette)?.label.toUpperCase() ?? 'NEBULA'}
            </button>
          ) : null}
          <button type="button" onClick={() => navigate({ to: '/chat' })}>
            ⌂ 채팅으로
          </button>
        </div>
      </div>

      {/* Hint */}
      <div className="hint">drag · scroll zoom · click node · F focus · ESC close</div>

      {/* Side panel */}
      <div className={`panel ${selected ? 'open' : ''}`}>
        {selected ? (
          <>
            <div className="panel-head">
              <div
                className="cat-dot"
                style={{
                  background: `oklch(${L} 0.15 ${selectedCatHue})`,
                  color: `oklch(${L} 0.15 ${selectedCatHue})`,
                }}
              />
              <div>
                <div className="meta">
                  {mapped.categories.find((c) => c.id === selected.category)?.label ?? '분류 없음'}
                </div>
              </div>
              <div className="id">{selected.id}</div>
              <button
                type="button"
                className="close"
                onClick={() => {
                  setSelected(null);
                  setFocusMode(false);
                }}
              >
                ×
              </button>
            </div>
            <div className="panel-body">
              {pageQuery.data ? (
                <>
                  <h1>{pageQuery.data.title}</h1>
                  <WikiMarkdown
                    takeaway={pageQuery.data.takeaway}
                    body={pageQuery.data.body}
                    resolvedSlugs={
                      new Set(pageQuery.data.outlinks.filter((o) => o.resolved).map((o) => o.slug))
                    }
                  />
                  <hr />
                  <button
                    type="button"
                    onClick={() =>
                      navigate({ to: '/wikis/p/$slug', params: { slug: selected.id } })
                    }
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--line-2)',
                      color: 'var(--fg)',
                      padding: '8px 14px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontSize: 12,
                      fontFamily: 'var(--font-mono)',
                      letterSpacing: '0.04em',
                    }}
                  >
                    전체 페이지 보기 →
                  </button>
                </>
              ) : pageQuery.isLoading ? (
                <p style={{ color: 'var(--fg-muted)' }}>본문 로딩 중…</p>
              ) : (
                <p style={{ color: 'var(--fg-muted)' }}>본문을 가져올 수 없습니다.</p>
              )}
            </div>
            {related.length > 0 ? (
              <div className="panel-related">
                <h4>Linked · {related.length}</h4>
                <div className="related-list">
                  {related.map((n) => {
                    const cat = mapped.categories.find((c) => c.id === n.category);
                    const color = `oklch(${L} 0.15 ${cat?.hue ?? 280})`;
                    return (
                      <button
                        key={n.id}
                        type="button"
                        className="related-item"
                        style={{ background: 'transparent', border: 0, textAlign: 'left' }}
                        onClick={() => focusOn(n.id)}
                      >
                        <span className="rdot" style={{ background: color, color }} />
                        <span>{n.title}</span>
                        <span className="rcat">{cat?.label ?? '분류 없음'}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
            {similarQuery.data?.enabled && similarQuery.data.results.length > 0 ? (
              <div className="panel-related">
                <h4>Similar · 의미 유사</h4>
                <div className="related-list">
                  {similarQuery.data.results.map((s) => (
                    <button
                      key={s.slug}
                      type="button"
                      className="related-item"
                      style={{ background: 'transparent', border: 0, textAlign: 'left' }}
                      onClick={() => focusOn(s.slug)}
                    >
                      <span
                        className="rdot"
                        style={{ background: 'var(--link)', color: 'var(--link)' }}
                      />
                      <span>{s.title}</span>
                      <span className="rcat">{(1 - s.distance).toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {/* Tooltip */}
      {hover && hover.node !== selected ? (
        <div className="tooltip" style={{ left: hover.sx + 14, top: hover.sy + 14 }}>
          <div className="ttitle">{hover.node.title}</div>
          <div
            className="tcat"
            style={{
              color: `oklch(${L} 0.15 ${
                mapped.categories.find((c) => c.id === hover.node.category)?.hue ?? 280
              })`,
            }}
          >
            {mapped.categories.find((c) => c.id === hover.node.category)?.label ?? '분류 없음'} ·{' '}
            {hover.node.degree} links
          </div>
        </div>
      ) : null}
    </div>
  );
}
